import React from 'react'
import SearchResultsToggle from './SearchResultsToggle'
import {
  Unstable_Grid2 as Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { SrpExperience } from '@src/utils/searchUtils'
import { makeStyles } from 'tss-react/mui'
import { FlagOutlined } from '@mui/icons-material'
import { useUserSession } from '@src/utils/useUserSession'
import getConfig from 'next/config'

const {
  publicRuntimeConfig: { featureFlags, srpCanaryCountries },
} = getConfig()

const useStyles = makeStyles()((theme) => ({
  bannerContainer: {
    width: '100%',
    alignItems: 'center',
    backgroundColor: '#FFF3D9',
    padding: theme.spacing(4, 6),
    marginBottom: theme.spacing(4),
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  flagContainer: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0),
    },
    display: 'flex',
    paddingRight: theme.spacing(1),
    flexShrink: 0,
    width: 32,
  },
  flagIcon: {
    fontSize: theme.typography.pxToRem(23),
    marginRight: theme.spacing(3),
  },
  textContainer: {
    alignItems: 'center',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
    },
  },
  bannerTextBase: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.4,
    padding: 0,
    margin: 0,
    color: theme.palette.text.primary,
    display: 'block',
  },
  bannerText1: {
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: theme.spacing(1),
      textAlign: 'center',
    },
  },
  bannerText2: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      textAlign: 'center',
      marginBottom: theme.spacing(2),
    },
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      alignSelf: 'center',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}))

interface SearchResultsToggleBannerProps {
  className?: string
  currentExperience: SrpExperience
}

const SearchResultsToggleBanner: React.FC<SearchResultsToggleBannerProps> = ({
  className = '',
  currentExperience,
}) => {
  const { classes, cx } = useStyles()
  const { userSession } = useUserSession()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const userCountry = userSession.country
  const isCanaryUser =
    srpCanaryCountries?.length === 0 ||
    srpCanaryCountries?.includes(userCountry)

  if (!isCanaryUser || !featureFlags.newSrp) {
    return null
  }

  return (
    <Grid
      container
      className={classes.bannerContainer}
      data-testid="search-results-toggle-banner"
    >
      <Grid container className={classes.textContainer} md={9}>
        {isDesktop && (
          <Grid className={classes.flagContainer}>
            <FlagOutlined color="secondary" className={classes.flagIcon} />
          </Grid>
        )}

        <Typography className={cx(classes.bannerTextBase, classes.bannerText1)}>
          {currentExperience === 'new' ? (
            <FormattedMessage
              id="SRP_BANNER_NEW_SEARCH"
              defaultMessage="We have upgraded our search experience, but you can still use our previous version."
            />
          ) : (
            <FormattedMessage
              id="SRP_BANNER_OLD_SEARCH"
              defaultMessage="We have an upgraded search experience. If you would like to try it, you can switch here."
            />
          )}
        </Typography>
      </Grid>
      <Grid className={cx(classes.toggle, className)}>
        <SearchResultsToggle
          currentExperience={currentExperience}
          className={className}
        />
      </Grid>
    </Grid>
  )
}

export default SearchResultsToggleBanner
